import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Table, TableColumn } from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useQuery } from '@tanstack/react-query';
import { styles } from './Projects.styles';
import { TableSecretData } from '../table/TableSecretData';
import { getApigeeCompanyApps } from '../../api/apigee';
import { ActionColumn } from '../applications/ApplicationsTable';

type Data = {
  id: string;
  name: string;
  displayName?: string;
  consumerKey: string;
  consumerSecret: string;
  developerName?: string;
  developerEmail?: string;
  company?: string;
  companyDisplayName?: string;
  resettable: boolean;
  removable: boolean;
};

const columns: TableColumn[] = [
  {
    title: 'Project / Developer',
    field: 'company',
    highlight: true,
    render: data => {
      const { company, companyDisplayName, developerName, developerEmail } =
        data as Data;

      if (company === undefined && companyDisplayName === undefined) {
        return (
          <Stack>
            <Typography variant="body2" fontWeight={700}>
              {developerName ?? developerEmail}
            </Typography>

            {developerName !== developerEmail && (
              <Typography variant="body2" fontSize={11}>
                {developerEmail}
              </Typography>
            )}
          </Stack>
        );
      }

      return (
        <Stack>
          <Typography variant="body2" fontWeight={700}>
            {companyDisplayName ?? company}
          </Typography>

          {companyDisplayName !== company && (
            <Typography variant="body2" fontSize={11}>
              {company}
            </Typography>
          )}
        </Stack>
      );
    },
  },
  {
    title: 'Client Name',
    field: 'name',
    render: data => (
      <Stack>
        <Typography variant="body2" fontWeight={700}>
          {(data as Data).displayName}
        </Typography>

        {(data as Data).displayName !== (data as Data).name && (
          <Typography variant="body2" fontSize={11}>
            {(data as Data).name}
          </Typography>
        )}
      </Stack>
    ),
  },
  {
    title: 'Client ID',
    field: 'consumerKey',
    render: data => <TableSecretData secret={(data as Data).consumerKey} />,
  },
  {
    title: 'Client Secret',
    field: 'consumerSecret',
    render: data => <TableSecretData secret={(data as Data).consumerSecret} />,
  },
  {
    title: 'Actions',
    width: 'auto',
    render: data => <ActionColumn data={data as Data} isApiProject />,
  },
];

export const ProjectsTable = () => {
  const discovery = useApi(discoveryApiRef);
  const identity = useApi(identityApiRef);

  const apigeeCompanyApps = useQuery({
    queryKey: ['apigeeCompanyApps'],
    queryFn: () => getApigeeCompanyApps(discovery, identity),
  });

  return (
    <div style={styles.tableContainer}>
      <Table
        options={{ paging: false }}
        data={Object.values(apigeeCompanyApps.data ?? []).flat()}
        isLoading={apigeeCompanyApps.isLoading}
        columns={columns}
        title="My API Projects"
        // @ts-ignore - subtitle can be an element but backstage expects string - https://github.com/backstage/backstage/blob/master/packages/core-components/src/components/Table/Table.tsx#L450
        subtitle={
          <Typography color="textSecondary" variant="body2">
            These are the clients that are associated with your project. These
            clients have been granted access to our UAT and production
            environments, allowing you to integrate them into your applications.
          </Typography>
        }
      />
    </div>
  );
};
